<template>
  <a-article
    v-if="isArticlePage"
    :article-data="articleData"
    :related-articles="relatedArticles"
    :related-videos="relatedVideos"
    :article-slug="articleSlug"
    :category-slug="categorySlug"
    :is-ebook-loaded="isEbookLoaded"
    :show-disclaimer="isDisclaimerVisible"
    @get-ebook-data="getEbookData"
  />

  <div v-else-if="IsSubCategory">
    <a-lazy-hydrate when-visible>
      <a-breadcrumbs v-bind="breadcrumbsProps">
        <a-visibility
          v-if="isDisclaimerVisible"
          show
          :from="$breakpoint.desktopMd"
        >
          <a-disclaimer
            :layout-ordering="$options.consts.disclaimerLayoutOrdering"
            :top-space-offset="$_hydrationHelpers_menuHeight"
          />
        </a-visibility>
      </a-breadcrumbs>
    </a-lazy-hydrate>
    <a-paginated-news-list
      ref="paginatedNewsList"
      page-type="subCategory"
      :pages-count="pagesCount"
      :pagination="pagination"
      :category-name="CategoryName"
      :subcategory-name="SubCategoryName"
      :most-viewed-articles="mostViewedArticles"
      :all-articles="articles"
      :in-progress="isRequestInProgress"
      show-tag
      @get-new-chunk="getNextArticleChunk"
    >
      <template v-slot:disclaimer>
        <a-visibility
          v-if="isDisclaimerVisible"
          show
          :to="$breakpoint.desktopSm"
        >
          <a-disclaimer
            :layout-ordering="$options.consts.disclaimerLayoutOrdering"
            :top-space-offset="$_hydrationHelpers_menuHeight"
            :bottom-space-offset="disclaimerBottomOffset"
          />
        </a-visibility>
      </template>
    </a-paginated-news-list>
    <a-sticky-banner
      :sticky-position="$options.consts.STICKY_BANNER_POSITION.BOTTOM"
      :banner-settings="
        $options.consts.BANNER_SETTINGS.ALL_NEWS.STICKY_TO_BOTTOM
      "
    />
    <a-pagination-links-for-google
      :page="pagination.page"
      :pages-count="pagesCount"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
    <a-url-pagination
      :item-count="articles.length"
      :pagination.sync="pagination"
      :initial-page="initialPage"
      :pages-count="pagesCount"
      :item-selector="$options.consts.ARTICLE_SLOT_SELECTOR"
      :non-paginated-url="canonicalUrlWithoutPagination"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { throwPageNotFoundError } from '@/plugins/error'
import mixins from '@/utils/mixins'
import {
  METHOD_NAME_TO_RUN_ON_LOGIN,
  METHOD_NAME_TO_RUN_ON_LOGOUT
} from '@/utils/mixins/run-method-on-login-logout'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import { ROUTE_NAME } from '@/utils/enums/routes'
import { PAGE_SCHEME_TYPE } from '@/utils/enums/pageSchemes'
import * as types from '@/store/mutation-types'
import ABreadcrumbs, { BREADCRUMB_NAMING } from 'shared/ABreadcrumbs'
import { STICKY_BANNER_POSITION } from 'enums/banners/sticky-banner'
import { BANNER_SETTINGS } from 'enums/banners/banner-settings'
import { getPageNumberFromRoute } from '@/plugins/helper'
import { applyFallback } from '@/utils/helpers'
import AStickyBanner from 'shared/AStickyBanner'
import APaginatedNewsList from 'shared/APaginatedNewsList'
import APaginationLinksForGoogle from 'shared/APaginationLinksForGoogle'
import AUrlPagination from 'shared/AUrlPagination'
import { LAYOUT as DISCLAIMER_LAYOUT } from 'shared/ADisclaimer/enums'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import {
  generatePaginatedCanonicalUrl,
  checkIfNonCanonicalPath
} from '@/utils/helpers/canonical-links'
import { generateCategoryUrl } from '@/utils/helpers/url-generators'
import { redirectHandler } from '@/utils/helpers/redirects/helpers'
import { handleNextChunks } from '@/utils/helpers/handle-duplicates'
import { pick } from 'ramda'
import { RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME } from 'enums/article'

/**
 * How many news should be taken from back-end for news by subcategory
 * @type {number}
 */
const DEFAULT_TAKE = 25

async function getArticlePageData({ ctx, route, store }) {
  const { dispatch } = store
  const { path: currentPath } = route
  const { subcategory: articleSlug } = route.params
  const { previewId } = route.query

  const articlePayload = {
    articleSlug: articleSlug.toLowerCase(),
    previewId
  }

  const { articleData, relatedArticles, relatedVideos } = await dispatch(
    'articles/requestArticlePageData',
    articlePayload
  )

  const { categorySlug, subcategorySlug, slug } = articleData

  const canonicalUrl = articleData.canonicalUrl

  if (
    !previewId &&
    checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })
  ) {
    redirectHandler({ ctx, to: canonicalUrl })
    return
  }

  const { EBOOK_LOCATION } = await import('shared/AEbook/enums')

  const ebookFetchProps = {
    location: EBOOK_LOCATION.ARTICLE,
    requestPayload: articlePayload
  }

  return {
    canonicalUrl,
    articleData,
    relatedArticles,
    relatedVideos,
    articleSlug: slug,
    categorySlug,
    subCategorySlug: subcategorySlug,
    ebookFetchProps
  }
}

async function getSubcategoryPageData({ ctx, route, store }) {
  const { path: currentPath } = route
  const initialPage = getPageNumberFromRoute(route)
  const { category: categorySlug, subcategory: subcategorySlug } = route.params
  const { dispatch } = store

  const pagination = {
    take: DEFAULT_TAKE,
    page: initialPage
  }

  const subcategoryPromises = [
    dispatch('articles/requestArticlesBySubcategory', {
      categorySlug,
      subCategorySlug: subcategorySlug,
      ...pagination
    }),
    dispatch('articles/requestMostViewedArticles', {
      isSponsored: store.getters['categories/checkIfThoughtLeadershipSlug'](
        categorySlug
      )
    })
  ]

  const [
    {
      Articles,
      Category: { Name, ParentName, Slug, ParentSlug },
      PagesCount
    },
    { mostViewedArticles }
  ] = await Promise.all(subcategoryPromises)

  const CategoryName = ParentName || Name
  const CategorySlug = ParentSlug || Slug
  const SubCategoryName = ParentName ? Name : null
  const SubCategorySlug = ParentSlug ? Slug : null

  if (PagesCount && initialPage > PagesCount) {
    throwPageNotFoundError(ctx, 'SubCategoryPage')
    return {}
  }

  const canonicalUrlWithoutPagination = generateCategoryUrl(
    { CategorySlug, SubCategorySlug },
    ctx
  )
  const canonicalUrl = generatePaginatedCanonicalUrl({
    canonicalUrlWithoutPagination,
    path: route.path
  })

  if (checkIfNonCanonicalPath(ctx, { currentPath, canonicalUrl })) {
    redirectHandler({ ctx, to: canonicalUrl })
    return
  }

  const { MOST_VIEWED_ARTICLES_MODEL } = await import('enums/responseModels')

  return {
    canonicalUrl,
    canonicalUrlWithoutPagination,
    articles: Articles,
    CategoryName,
    SubCategoryName,
    categorySlug: CategorySlug,
    subCategorySlug: SubCategorySlug,
    mostViewedArticles: applyFallback(
      mostViewedArticles,
      MOST_VIEWED_ARTICLES_MODEL
    ),
    initialPage,
    pagesCount: PagesCount,
    pagination
  }
}

function generateDefaultSubcategoryContentDataModel() {
  return {
    articles: [],
    mostViewedArticles: [],
    CategoryName: null,
    SubCategoryName: null
  }
}

function generateDefaultArticleContentDataModel() {
  return {
    articleData: null,
    relatedArticles: null,
    relatedVideos: []
  }
}

export default {
  name: 'SubCategoryPage',
  scrollToTop: true,
  mixins: [
    mixins.refreshDataHandler,
    mixins.runMethodOnLoginLogout,
    mixins.setPageLoaded,
    mixins.ebookDataFetch,
    mixins.urlGenerators,
    hydrationHelpers,
    mixins.refreshDataListenersFactory({
      refreshHandlerByType: {
        [PENDING_ACTION.SAVE_ARTICLE]: METHOD_NAME.UPDATE_IS_SAVED_VALUE,
        [PENDING_ACTION.FOLLOW_TAG]: METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE,
        [PENDING_ACTION.FOLLOW_AUTHOR]:
          METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE
      }
    }),
    mixins.delayedContentRefresh
  ],
  components: {
    AStickyBanner,
    APaginatedNewsList,
    AArticle: () => import('@/components/_sections/article/AArticle'),
    ABreadcrumbs,
    APaginationLinksForGoogle,
    ADisclaimer: hydrateWhenVisible(() => import('shared/ADisclaimer'), {
      props: ['layout-ordering', 'top-space-offset', 'bottom-space-offset']
    }),
    AUrlPagination
  },
  consts: {
    STICKY_BANNER_POSITION,
    BANNER_SETTINGS,
    disclaimerLayoutOrdering: [
      DISCLAIMER_LAYOUT.BOTTOM_LEFT,
      DISCLAIMER_LAYOUT.TOP_LEFT
    ],
    disclaimerBottomOffsetMobile: 50, // bottom banner on mobile
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`
  },
  head() {
    return this.$generateMetaTags({
      titleParam: this.SubCategoryName,
      descriptionParam: this.SubCategoryName,
      schemes: [{ type: PAGE_SCHEME_TYPE.ORGANIZATION }],
      canonicalUrl: this.canonicalUrl
    })
  },
  async asyncData(ctx) {
    const { fullPath } = ctx.route
    const { previewId } = ctx.route.query
    const { subcategory: subcategoryOrArticleSlug } = ctx.route.params

    try {
      const dispatch = ctx.store.dispatch

      const { IsArticle, IsSubCategory } = previewId
        ? { IsArticle: true, IsSubCategory: false }
        : await dispatch('articles/requestCheckSlug', {
            slug: subcategoryOrArticleSlug
          })

      if (!IsArticle && !IsSubCategory) {
        throwPageNotFoundError(ctx, 'SubCategoryPage')
        return {}
      }

      const initialData = {
        IsArticle,
        IsSubCategory
      }

      const asyncData = IsSubCategory
        ? await getSubcategoryPageData({
            ctx,
            route: ctx.route,
            store: ctx.store
          })
        : await getArticlePageData({
            ctx,
            route: ctx.route,
            store: ctx.store
          })

      return {
        ...initialData,
        ...asyncData
      }
    } catch (err) {
      ctx.app.$errorHandler(err, ctx, {
        showErrorPage: true,
        showMessage: false,
        componentName: 'SubCategoryPage'
      })
    } finally {
      await ctx.store.dispatch('router/removeRouteFromQueue', fullPath)
    }
  },
  data() {
    const initialPage = getPageNumberFromRoute(this.$route)

    return {
      ...generateDefaultSubcategoryContentDataModel(),
      ...generateDefaultArticleContentDataModel(),
      canonicalUrl: this.$route.path,
      canonicalUrlWithoutPagination: this.$route.path,
      CategoriesStrip: [],
      articleSlug: null,
      categorySlug: null,
      subCategorySlug: null,
      IsArticle: null,
      IsSubCategory: null,
      inProgress: false,
      initialPage,
      pagesCount: 0,
      pagination: {
        take: DEFAULT_TAKE,
        page: initialPage
      },
      shift: 0,
      isEbookLoaded: false,
      disclaimerBottomOffsetByBreakpoint: {
        [this.$breakpoint.mobile]: this.$options.consts
          .disclaimerBottomOffsetMobile
      }
    }
  },
  computed: {
    ...mapGetters({
      checkIfThoughtLeadershipSlug: 'categories/checkIfThoughtLeadershipSlug'
    }),
    isRequestInProgress() {
      return this.inProgress || this.isPendingContentRefresh
    },
    breadcrumbsDynamicNames() {
      return {
        [ROUTE_NAME.CATEGORY]: this.CategoryName,
        [ROUTE_NAME.CATEGORY_SUBCATEGORY]: this.SubCategoryName
      }
    },
    breadcrumbsProps() {
      return {
        breadcrumbsDynamicNames: this.breadcrumbsDynamicNames,
        breadcrumbsNaming: BREADCRUMB_NAMING.KEEP_AS_IS
      }
    },
    isArticlePage() {
      return (
        !this.IsSubCategory &&
        this.IsArticle &&
        this.$helper.isNotAmpPage(this.$route.name)
      )
    },
    isDisclaimerVisible() {
      return this.checkIfThoughtLeadershipSlug(this.categorySlug)
    },
    disclaimerBottomOffset() {
      return (
        this.$_hydrationHelpers_getValueByBreakpoint(
          this.disclaimerBottomOffsetByBreakpoint
        ) || 0
      )
    }
  },
  methods: {
    ...mapActions({
      requestArticlesBySubcategory: 'articles/requestArticlesBySubcategory'
    }),
    async getArticles(pagination) {
      try {
        const {
          Articles,
          PagesCount
        } = await this.requestArticlesBySubcategory({
          categorySlug: this.$route.params.category,
          subCategorySlug: this.$helper.getLastPathParam(this.$route.path),
          ...pagination
        })

        this.pagesCount = PagesCount
        return Articles
      } catch (err) {
        this.$errorHandler(err, this, {
          showErrorPage: true,
          showMessage: false
        })
      }
    },
    getNextArticleChunk() {
      this.pagination.page++
      handleNextChunks({
        shift: this.shift,
        pagination: this.pagination,
        pagesCount: this.pagesCount,
        setInProgressTrueFn: () => {
          this.inProgress = true
        },
        setInProgressFalseFn: () => {
          this.inProgress = false
        },
        setShiftFn: newVal => {
          this.shift = newVal
        },
        requestNextChunkFn: this.getArticles.bind(this),
        addNewArticlesFn: async newArticles => {
          this.articles = [...this.articles, ...newArticles]
          await this.$nextTick()
        },
        getExistingArticleIdsFn: () => this.articles.map(({ id }) => id)
      })
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_AUTHOR_VALUE]({ id, value }) {
      this.articleData.publishers = this.articleData.publishers.map(
        publisher => {
          if (publisher.id === id) {
            return {
              ...publisher,
              isFollowed: value
            }
          }

          return publisher
        }
      )
    },
    [METHOD_NAME.UPDATE_IS_SAVED_VALUE](payload) {
      if (this.isArticlePage) {
        this.articleData.isSaved = payload.value
      } else {
        this.articles = this.$_refreshDataHandler_updateIsSavedValue(payload)
      }
    },
    [METHOD_NAME.UPDATE_IS_FOLLOWED_TAG_VALUE](payload) {
      if (this.isArticlePage) {
        const { id, value } = payload
        if (this.articleData.mainTag.id !== id) return

        this.articleData.mainTag = {
          ...this.articleData.mainTag,
          isFollowed: value
        }
        this.articleData.tags = this.articleData.tags.map(tagData => {
          if (tagData.id === id) return this.articleData.mainTag

          return tagData
        })
      } else {
        this.articles = this.$_refreshDataHandler_updateIsFollowedTagValue(
          payload
        )
      }
    },
    [METHOD_NAME_TO_RUN_ON_LOGOUT]() {
      if (this.IsSubCategory) {
        this.articles = this.$_refreshDataHandler_resetActionIconValues()
      }

      if (this.IsArticle) {
        this.articleData.isSaved = false
        this.articleData.tags = this.articleData.tags.map(tag => ({
          ...tag,
          isFollowed: false
        }))
        this.articleData.publishers = this.articleData.publishers.map(
          publisher => ({ ...publisher, isFollowed: false })
        )
      }
    },
    [METHOD_NAME_TO_RUN_ON_LOGIN]() {
      if (this.IsSubCategory) {
        this.updateSubCategoryActionIconStates()
      } else {
        this.updateArticleActionIconStates()
      }
    },
    async updateSubCategoryActionIconStates() {
      const {
        Article,
        Tag
      } = await this.$_refreshDataHandler_getSavedIdsOfEntities([
        this.$_refreshDataHandler_savedEntity.ARTICLE,
        this.$_refreshDataHandler_savedEntity.TAG
      ])
      this.articles = this.$_refreshDataHandler_updateArticleSavedEntities({
        articleIds: Article,
        tagIds: Tag
      })
    },
    async updateArticleActionIconStates() {
      const payload = {
        articleSlug: this.$helper.getLastPathParam(this.$route.path)
      }

      const {
        isSaved = this.articleData.isSaved,
        authorsIsFollowed = this.articleData.publishers.map(
          ({ isFollowed }) => isFollowed
        ),
        tags = this.articleData.tags,
        mainTag = this.articleData.mainTag
      } = await this.$_refreshDataHandler_getSingleArticleEntities(payload)

      this.articleData = {
        ...this.articleData,
        isSaved,
        publishers: this.articleData.publishers.map((publisher, index) => ({
          ...publisher,
          isFollowed: authorsIsFollowed[index]
        })),
        tags,
        mainTag
      }
    },
    async getEbookData() {
      const { location, requestPayload } = this.ebookFetchProps || {}

      this.articleData.Ebook = await this.$_ebookDataFetch_getEbook(
        location,
        requestPayload
      )

      this.isEbookLoaded = true
    },
    refreshContentForSubcategoryPage() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultSubcategoryContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getSubcategoryPageData({
            ctx: this,
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['lastModified'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
          this.$refs.paginatedNewsList.rerenderNonHydratedComponents()
        }
      })
    },
    refreshContentForArticlePage() {
      const dataKeysForDelayedContentRefresh = Object.keys(
        generateDefaultArticleContentDataModel()
      )

      this.$_delayedContentRefresh_run({
        getOldDataFn: () => pick(dataKeysForDelayedContentRefresh, this),
        requestNewDataFn: async () => {
          const newData = await getArticlePageData({
            ctx: this,
            route: this.$route,
            store: this.$store
          })

          return pick(dataKeysForDelayedContentRefresh, newData)
        },
        dataComparisonKeysToOmitDeep: ['ModifiedOn'],
        dataChangedCallbackFn: newData => {
          Object.assign(this, pick(dataKeysForDelayedContentRefresh, newData))
          this.$bus.$emit(RERENDER_NON_HYDRATED_ARTICLE_COMPONENTS_BUS_NAME)
        }
      })
    },
    refreshContentForSubcategoryOrArticlePage() {
      if (this.IsArticle) {
        this.refreshContentForArticlePage()
      } else {
        this.refreshContentForSubcategoryPage()
      }
    }
  },
  mounted() {
    this.refreshContentForSubcategoryOrArticlePage()
  },
  beforeDestroy() {
    this.$store.commit(`articles/${types.SET_IS_CURRENT_PAGE_ARTICLE}`, false)
  }
}
</script>
